/* @flow */
import React, {Component} from 'react';
import {type ApolloClient, withApollo} from 'react-apollo';
import {compose} from 'redux';
import {fetchListingImages} from './hitCarouselQueryService';
import SimpleImageCarousel from '@homeaway/react-simple-image-carousel';
import {
    PHOTOCAROUSEL_LEFT_LABEL,
    PHOTOCAROUSEL_RIGHT_LABEL,
    triggerCarouselEDAPEvent,
    triggerCarouselImagePositionEdapEvent
} from '../../utils/carouselEdapUtils';

import './HitCarouselContainer.less';

export type HitImage = {
    lowResSrc?: string,
    src?: string,
    altText?: string
};

type State = {
    images: Array<HitImage>,
    hasFetchedImages: boolean,
    imageIndex: number
};

type Props = {
    client: ApolloClient,
    detailPageUrl: string,
    edapAction: string,
    images: HitImage[],
    listingId: string,
    onImageClick?: Function,
    totalImageCount: number
};

const INDEX_OF_LAST_IMAGE = 2;

function transformImages(images, totalImageCount) {
    const imageLength = images.length;
    let arr = [...images];

    if (imageLength < totalImageCount) {
        for (let i = imageLength; i < totalImageCount; i++) {
            arr.push({});
        }
    }

    arr.push(arr.splice(INDEX_OF_LAST_IMAGE, 1)[0]);

    return arr;
}

export class HitCarouselContainer extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            images: transformImages(props.images, props.totalImageCount),
            hasFetchedImages: false,
            imageIndex: 0
        };
    }

    onImageNavigate = (index: number) => {
        const {edapAction} = this.props;
        const currentIndex = this.state.imageIndex;

        this.setState({imageIndex: index});
        const imageSrc = this.state.images[index].src;

        triggerCarouselEDAPEvent(
            edapAction,
            currentIndex < index
                ? PHOTOCAROUSEL_RIGHT_LABEL
                : PHOTOCAROUSEL_LEFT_LABEL
        );

        if (imageSrc) {
            triggerCarouselImagePositionEdapEvent(`${imageSrc}:${index}`);
        }

        this.populateRemainingImages();
    };

    // returns a promise for testing purposes only
    populateRemainingImages: () => Promise<*> = () => {
        const {client, listingId, totalImageCount} = this.props;
        const {images, imageIndex} = this.state;
        const imageLength = images.length;

        if (this.state.hasFetchedImages) {
            return Promise.resolve({
                populatedIndex: imageIndex,
                populatedImagesLength: imageLength
            });
        }

        // only attempt to fetch images once
        this.setState({
            hasFetchedImages: true
        });

        return fetchListingImages({client, listingId}).then((listingImages) => {
            if (listingImages && listingImages.length) {
                this.setState({
                    images: transformImages(listingImages, totalImageCount)
                });
            }

            return Promise.resolve({
                populatedIndex: imageIndex,
                populatedImagesLength: imageLength
            });
        });
    };

    navigateToListing = () => {
        const {detailPageUrl, onImageClick} = this.props;
        if (onImageClick) {
            onImageClick();
        }
        // TODO: remove/refactor upon incorporating anchorHref functionality for carousels
        // see https://github.homeawaycorp.com/traveler/react-simple-image-carousel/issues/1
        // and https://github.homeawaycorp.com/traveler/react-image-carousel/issues/14
        // Why? - Back button will not close a new tab when using window.open
        window.open(detailPageUrl, '_blank');
    };

    render() {
        return (
            <SimpleImageCarousel
                images={this.state.images}
                index={this.state.imageIndex}
                transitionStyle="slide"
                transitionDuration={250}
                onNextClick={this.onImageNavigate}
                onPrevClick={this.onImageNavigate}
                onClick={this.navigateToListing}
            />
        );
    }
}

export default compose(withApollo)(HitCarouselContainer);
